import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InvoiceTable = ({ invoices, onViewInvoice, onAddPayment, onSendInvoice, onBulkAction, onStatusChange }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0)?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  const getStatusConfig = (status) => {
    const s = status?.toLowerCase();
    switch (s) {
      case 'draft': return { color: 'bg-muted text-muted-foreground border-border', icon: 'FileEdit', label: 'Draft' };
      case 'sent': return { color: 'bg-primary/10 text-primary border-primary/20', icon: 'Send', label: 'Sent' };
      case 'pending': return { color: 'bg-warning/10 text-warning border-warning/20', icon: 'Clock', label: 'Pending' };
      case 'partial': return { color: 'bg-blue-500/10 text-blue-600 border-blue-200', icon: 'PieChart', label: 'Partial' };
      case 'paid': return { color: 'bg-success/10 text-success border-success/20', icon: 'CheckCircle', label: 'Paid' };
      case 'overdue': return { color: 'bg-error/10 text-error border-error/20', icon: 'AlertTriangle', label: 'Overdue' };
      case 'cancelled': return { color: 'bg-muted text-muted-foreground border-border', icon: 'XCircle', label: 'Cancelled' };
      default: return { color: 'bg-muted text-muted-foreground border-border', icon: 'Circle', label: status || 'Unknown' };
    }
  };

  const getEffectiveStatus = (invoice) => {
    return invoice?.invoice_status || invoice?.status?.name || 'draft';
  };

  const isOverdue = (invoice) => {
    if (!invoice?.due_date) return false;
    const status = getEffectiveStatus(invoice);
    if (['paid', 'cancelled']?.includes(status?.toLowerCase())) return false;
    return new Date(invoice?.due_date) < new Date();
  };

  // Filter and search
  const filtered = invoices?.filter(inv => {
    const status = getEffectiveStatus(inv)?.toLowerCase();
    const matchesStatus = statusFilter === 'all' || status === statusFilter;
    const q = searchQuery?.toLowerCase();
    const matchesSearch = !q ||
      inv?.invoice_number?.toLowerCase()?.includes(q) ||
      inv?.customer?.name?.toLowerCase()?.includes(q) ||
      inv?.customer?.email?.toLowerCase()?.includes(q);
    return matchesStatus && matchesSearch;
  })?.sort((a, b) => {
    let aVal, bVal;
    if (sortField === 'total_amount') { aVal = parseFloat(a?.total_amount || 0); bVal = parseFloat(b?.total_amount || 0); }
    else if (sortField === 'due_date') { aVal = new Date(a?.due_date || 0); bVal = new Date(b?.due_date || 0); }
    else { aVal = new Date(a?.created_at || 0); bVal = new Date(b?.created_at || 0); }
    return sortDir === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev?.includes(id) ? prev?.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds?.length === filtered?.length) setSelectedIds([]);
    else setSelectedIds(filtered?.map(inv => inv?.id));
  };

  const handleBulkAction = (action) => {
    if (selectedIds?.length === 0) return;
    onBulkAction?.(action, selectedIds);
    setSelectedIds([]);
  };

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'sent', label: 'Sent' },
    { value: 'pending', label: 'Pending' },
    { value: 'partial', label: 'Partial' },
    { value: 'paid', label: 'Paid' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  const SortIcon = ({ field }) => (
    <span className="ml-1 inline-flex flex-col">
      <Icon
        name={sortField === field && sortDir === 'asc' ? 'ChevronUp' : 'ChevronsUpDown'}
        size={12}
        className={sortField === field ? 'text-primary' : 'text-muted-foreground'}
      />
    </span>
  );

  return (
    <div className="space-y-4">
      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by invoice #, customer name or email..."
            value={searchQuery}
            onChange={e => setSearchQuery(e?.target?.value)}
            className="w-full pl-9 pr-4 py-2 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
              <Icon name="X" size={14} className="text-muted-foreground hover:text-foreground" />
            </button>
          )}
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e?.target?.value)}
          className="px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {statusOptions?.map(opt => (
            <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
          ))}
        </select>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="Filter" size={14} />
          <span>{filtered?.length} of {invoices?.length}</span>
        </div>
      </div>

      {/* Bulk Operations Bar */}
      {selectedIds?.length > 0 && (
        <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
          <span className="text-sm font-medium text-primary">{selectedIds?.length} selected</span>
          <div className="flex items-center gap-2 ml-auto">
            <Button size="sm" variant="outline" onClick={() => handleBulkAction('send')} iconName="Send" iconPosition="left">
              Send All
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleBulkAction('mark_paid')} iconName="CheckCircle" iconPosition="left">
              Mark Paid
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleBulkAction('mark_overdue')} iconName="AlertTriangle" iconPosition="left">
              Mark Overdue
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])} iconName="X">
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={filtered?.length > 0 && selectedIds?.length === filtered?.length}
                  onChange={toggleSelectAll}
                  className="rounded border-border"
                />
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => toggleSort('invoice_number')}>
                Invoice # <SortIcon field="invoice_number" />
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Customer</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => toggleSort('created_at')}>
                Date <SortIcon field="created_at" />
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => toggleSort('due_date')}>
                Due Date <SortIcon field="due_date" />
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => toggleSort('total_amount')}>
                Total <SortIcon field="total_amount" />
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Amount Due</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered?.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <Icon name="FileText" size={32} className="text-muted-foreground" />
                    <p className="text-muted-foreground">No invoices found</p>
                    {(searchQuery || statusFilter !== 'all') && (
                      <button
                        onClick={() => { setSearchQuery(''); setStatusFilter('all'); }}
                        className="text-sm text-primary hover:underline"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              filtered?.map((invoice) => {
                const effectiveStatus = getEffectiveStatus(invoice);
                const statusCfg = getStatusConfig(effectiveStatus);
                const overdue = isOverdue(invoice);
                const isSelected = selectedIds?.includes(invoice?.id);

                return (
                  <tr
                    key={invoice?.id}
                    className={`border-b border-border last:border-0 hover:bg-muted/30 transition-smooth ${
                      isSelected ? 'bg-primary/5' : ''
                    } ${overdue ? 'border-l-2 border-l-error' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(invoice?.id)}
                        className="rounded border-border"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => onViewInvoice(invoice)}
                        className="text-sm font-medium text-primary hover:underline"
                      >
                        {invoice?.invoice_number}
                      </button>
                      {invoice?.sent_at && (
                        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                          <Icon name="Send" size={10} />
                          Sent {formatDate(invoice?.sent_at)}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-foreground">{invoice?.customer?.name}</p>
                      <p className="text-xs text-muted-foreground">{invoice?.customer?.email}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{formatDate(invoice?.created_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-sm ${overdue ? 'text-error font-medium' : 'text-foreground'}`}>
                        {formatDate(invoice?.due_date)}
                      </span>
                      {overdue && (
                        <p className="text-xs text-error">
                          {Math.floor((new Date() - new Date(invoice?.due_date)) / (1000 * 60 * 60 * 24))}d overdue
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-foreground">{formatCurrency(invoice?.total_amount)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-medium ${
                        parseFloat(invoice?.amount_due || 0) > 0 ? 'text-error' : 'text-success'
                      }`}>
                        {formatCurrency(invoice?.amount_due)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${statusCfg?.color}`}>
                        <Icon name={statusCfg?.icon} size={11} />
                        {statusCfg?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => onViewInvoice(invoice)}
                          className="p-1.5 rounded hover:bg-muted transition-smooth"
                          title="View Details"
                        >
                          <Icon name="Eye" size={14} className="text-muted-foreground hover:text-foreground" />
                        </button>
                        {effectiveStatus !== 'paid' && effectiveStatus !== 'cancelled' && (
                          <button
                            onClick={() => onSendInvoice?.(invoice)}
                            className="p-1.5 rounded hover:bg-muted transition-smooth"
                            title="Send Invoice"
                          >
                            <Icon name="Send" size={14} className="text-muted-foreground hover:text-primary" />
                          </button>
                        )}
                        {effectiveStatus !== 'paid' && effectiveStatus !== 'cancelled' && (
                          <button
                            onClick={() => onAddPayment(invoice)}
                            className="p-1.5 rounded hover:bg-muted transition-smooth"
                            title="Record Payment"
                          >
                            <Icon name="DollarSign" size={14} className="text-muted-foreground hover:text-success" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvoiceTable;